// Draws one beat of a cartoon into the Studio's canvas, in the flat (2D) or the solid (3D) look, with the camera that suits it.
import { KINDS } from "./cast";
import { characterAt } from "./staging";
import { drawBackground, drawCharacter, drawNameTag } from "./toon2d";
import { blinkAt, blinkSchedule } from "../lipsync";

const clamp = (x, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));
const ease = (x) => { const t = clamp(x); return t * t * (3 - 2 * t); };

let solid = null;                                              // the 3D renderer, once it has been loaded (it brings three.js with it)

// Loads the 3D renderer; resolves with false when this browser cannot draw in 3D (the video is then drawn flat).
export async function loadSolid() {
  if (solid) return true;
  try {
    const mod = await import("./toon3d");
    if (!mod.supported()) return false;
    solid = mod;
    return true;
  } catch {
    return false;
  }
}
export const solidReady = () => !!solid;

// Where the camera is: `zoom` (1 = the whole stage) and `cx` (0..1, what it looks at). The first beat of a scene shows the whole stage, then the camera comes in on whoever speaks.
export function shotFor(beat, local, speakerX, aspect = 16 / 9) {
  const u = clamp(local / Math.max(0.5, beat.seconds));
  if (beat.sceneStart || speakerX === null) return { zoom: 1 + 0.05 * u, cx: 0.5 };
  const zoom = 1 + (0.16 + 0.1 * ease(u)) * (aspect < 1 ? 0.4 : 1);                // a tall picture shows less of the stage, so the camera comes in less
  return { zoom, cx: 0.5 + (speakerX - 0.5) * 0.7 };
}

export function newToonState(cast, seed = 7) {
  const blinks = {};
  cast.forEach((c, i) => { blinks[c.id] = blinkSchedule(seed + i * 13, 900); });
  return blinks;
}

// toon: { mode: "2d" | "3d", beat (a staged beat), cast: { id: member }, blinks, order: [ids] }; `local` = seconds into the beat; `speech` = lipsync.mouthAt for the speaker.
export function drawToonBeat(ctx, W, H, toon, local, time, speech) {
  const { beat, cast, blinks } = toon;
  const ids = Object.keys(beat.chars);
  const speakerX = beat.speaker && beat.chars[beat.speaker] ? characterAt(beat, beat.speaker, local, speech).x : null;
  const shot = shotFor(beat, local, speakerX, W / H);
  const placed = ids.map((id) => ({ id, member: cast[id], state: characterAt(beat, id, local, speech) })).filter((p) => p.member && p.state && p.state.visible);
  placed.sort((a, b) => (a.id === beat.speaker) - (b.id === beat.speaker));

  if (toon.mode === "3d" && solid) {
    const frame = solid.drawSolidBeat(W, H, { beat, placed, setting: beat.setting, time, shot, blinks, local });
    if (frame) {
      ctx.drawImage(frame, 0, 0, W, H);
      const tag = beat.speaker && solid.tagOf(beat.speaker);
      if (tag && cast[beat.speaker]) drawNameTag(ctx, cast[beat.speaker].name, tag.x, tag.y, Math.max(14, H * 0.03));
      tint(ctx, W, H, beat.time);
      return;
    }
  }

  ctx.save();
  ctx.translate(W / 2, H * 0.62);
  ctx.scale(shot.zoom, shot.zoom);
  ctx.translate(-W * shot.cx, -H * 0.62);
  const horizon = 0.78;
  drawBackground(ctx, W, H, beat.setting, time, horizon);
  ctx.restore();

  const count = Math.max(2, ids.length);
  const unit = Math.min(H * 0.1, (W * 0.6) / Math.max(1, count - 1) / 2.6);
  const feetY = H * 0.9;
  placed.forEach(({ id, member, state }) => {
    const info = KINDS[member.kind] || KINDS.man;
    const s = unit * info.scale * shot.zoom;
    const px = W / 2 + (state.x - shot.cx) * W * shot.zoom;
    const py = H * 0.62 + (feetY - H * 0.62) * shot.zoom;
    ctx.save();
    ctx.translate(px, py);
    ctx.scale(s * state.facing, s);
    const lipsOpen = state.speaking && speech ? speech.open : 0;
    drawCharacter(ctx, member, { blink: blinkAt(blinks[id] || [], time), open: lipsOpen, wide: state.wide, round: state.round, look: state.facing * 0.4 }, state.pose, time);
    ctx.restore();
  });
  drawTags(ctx, W, H, placed, beat, shot);
  tint(ctx, W, H, beat.time);
}

function drawTags(ctx, W, H, placed, beat, shot) {
  const speaker = placed.find((p) => p.id === beat.speaker);
  if (!speaker) return;
  const info = KINDS[speaker.member.kind] || KINDS.man;
  const unit = Math.min(H * 0.1, (W * 0.6) / Math.max(1, Math.max(2, placed.length) - 1) / 2.6);
  const px = W / 2 + (speaker.state.x - shot.cx) * W * shot.zoom;
  const top = H * 0.62 + (H * 0.9 - H * 0.62) * shot.zoom - unit * info.scale * shot.zoom * 6.7;
  drawNameTag(ctx, speaker.member.name, px, top, Math.max(14, H * 0.03));
}

function tint(ctx, W, H, time) {
  if (time === "evening") { ctx.fillStyle = "rgba(255,120,40,0.16)"; ctx.fillRect(0, 0, W, H); }
  else if (time === "night") { ctx.fillStyle = "rgba(10,20,70,0.3)"; ctx.fillRect(0, 0, W, H); }
}
